import { Link, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from './auth';
import { AdminSubNav } from './AdminSubNav';
import { NotificationBanners } from './NotificationBanners';
import { api } from './api';

declare global {
  interface Window {
    Osano?: {
      cm?: {
        showDrawer?: (id: string) => void;
        showDoNotSell?: () => void;
      };
    };
  }
}

/** Open the Osano cookie-preferences drawer (mirrors .old footer onclick). */
function openCookiePreferences() {
  window.Osano?.cm?.showDrawer?.('osano-cm-dom-info-dialog-open');
}

/** Open the Osano "Do Not Sell" dialog (mirrors .old footer onclick). */
function openDoNotSell() {
  window.Osano?.cm?.showDoNotSell?.();
}

/**
 * Admin / management SPA shell.
 *
 * Same top navbar markup as the end-user `WizardLayout` (mirrors
 * `.old/resources/views/layouts/shared/horizontal-nav.blade.php`) but with
 * an additional secondary nav row showing the admin tabs the legacy app
 * exposed in its left sidebar.
 */

const ACTIVE_TRAFFIC_KEY = 'fs.activeTraffic.lastDay';
const UNFILTERED_TRAFFIC_KEY = 'fs.unfilteredTraffic.lastDay';

function pingOncePerDay(storageKey: string, label: string, attributeId: number) {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const last = sessionStorage.getItem(storageKey);
    if (last === today) return;
    sessionStorage.setItem(storageKey, today);
    api.post('/api/v1/sitestats', { label, attributeId }).catch(() => {
      sessionStorage.removeItem(storageKey);
    });
  } catch {
    // sessionStorage unavailable in some contexts — silently skip.
  }
}

function useActiveTrafficPing(attributeId: number | undefined) {
  useEffect(() => {
    if (typeof window === 'undefined' || !attributeId) return;
    pingOncePerDay(ACTIVE_TRAFFIC_KEY, 'active traffic', attributeId);
    pingOncePerDay(UNFILTERED_TRAFFIC_KEY, 'unfiltered traffic', attributeId);
  }, [attributeId]);
}

export function AppShell() {
  const { user, logout } = useAuth();
  useActiveTrafficPing(user?.attributeId);
  const [tosOpen, setTosOpen] = useState(false);

  return (
    <div className="wrapper">
      <div className="content-page">
        <div className="content d-xl-block footerspacer">
          {/* Topbar */}
          <div className="navbar-custom topnav-navbar shadow-sm mb-1 clearfix">
            <div className="container-fluid d-flex d-lg-none justify-content-between">
              <div style={{ width: 65 }}>&nbsp;</div>
              <div>
                <Link to="/" className="topnav-logo">
                  <img
                    src="/attribute/ads.png"
                    alt=""
                    className="img-fluid"
                    style={{ maxHeight: '5vh', minHeight: 70 }}
                  />
                </Link>
              </div>
              <div style={{ width: 65 }}>&nbsp;</div>
            </div>

            <div className="container-fluid d-none d-lg-block">
              <Link to="/" className="topnav-logo">
                <span className="topnav-logo-lg">
                  <img src="/attribute/ads.png" alt="" height={70} />
                </span>
              </Link>
              <Link to="/admin" className="topnav-text">
                <span>FlexStorm Project Planner</span>
              </Link>

              <ul className="list-unstyled topbar-menu float-end mb-0 d-none d-lg-block">
                <li className="notification-list">
                  <Link
                    className="nav-link bg-dark text-white arrow-none px-3 me-4"
                    style={{ marginTop: 20, paddingTop: 5, paddingBottom: 5 }}
                    to="/"
                  >
                    <span>Return to Menu</span>
                  </Link>
                </li>

                {user ? (
                  <li className="dropdown notification-list">
                    <a
                      className="nav-link dropdown-toggle arrow-none me-0"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}
                      data-bs-toggle="dropdown"
                      href="#"
                      role="button"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <span className="account-user-avatar">
                        <img
                          src="/assets/images/Icon_Profile.png"
                          alt="user-image"
                          className="rounded-circle"
                          style={{ height: 32, width: 32 }}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </span>
                      <span>
                        <span className="account-user-name text-capitalize">
                          {user.name || 'Account'}
                        </span>
                      </span>
                    </a>
                    <div className="dropdown-menu dropdown-menu-end dropdown-menu-animated topbar-dropdown-menu profile-dropdown">
                      <div className="dropdown-header noti-title">
                        <h6 className="text-overflow m-0">Welcome!</h6>
                      </div>
                      <button
                        type="button"
                        className="dropdown-item notify-item"
                        onClick={() => logout()}
                      >
                        <i className="mdi mdi-logout me-1" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>

          {/* Secondary admin nav bar — legacy parity */}
          <AdminSubNav />

          <NotificationBanners />

          <main>
            <Outlet />
          </main>
        </div>
      </div>

      <footer className="footer">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="text-md-center footer-links d-block">
                <span className="lh-lg me-0 me-lg-4">
                  &copy; {new Date().getFullYear()} Advanced Drainage Systems&trade;. All&nbsp;rights&nbsp;reserved.
                </span>
                <br className="d-lg-none" />
                <a className="lh-lg py-1 ms-0" target="_blank" rel="noreferrer" href="https://www.adspipe.com/legal">
                  Legal
                </a>
                <span className="d-lg-inline d-none">&nbsp;|&nbsp;</span>
                <br className="d-lg-none" />
                <a
                  className="lh-lg py-1 ms-0"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setTosOpen(true);
                  }}
                >
                  Terms&nbsp;and&nbsp;Conditions
                </a>
                <span className="d-md-inline d-none">&nbsp;|&nbsp;</span>
                <br className="d-lg-none" />
                <a
                  className="lh-lg py-1 ms-0"
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.adspipe.com/privacy-policy"
                >
                  Privacy&nbsp;Policy
                </a>
                <span className="d-md-inline d-none">&nbsp;|&nbsp;</span>
                <br className="d-lg-none" />
                <a
                  className="lh-lg py-1 ms-0"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openCookiePreferences();
                  }}
                >
                  Manage&nbsp;My&nbsp;Cookie&nbsp;Preferences
                </a>
                <span className="d-md-inline d-none">&nbsp;|&nbsp;</span>
                <br className="d-lg-none" />
                <a
                  className="lh-lg py-1 ms-0"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openDoNotSell();
                  }}
                >
                  Do&nbsp;Not&nbsp;Sell&nbsp;My&nbsp;Information
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms of Use modal — mirrors the #tos modal in .old horizontal.blade.php */}
      {tosOpen && (
        <div
          className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          tabIndex={-1}
          onClick={(e) => {
            if (e.target === e.currentTarget) setTosOpen(false);
          }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content border" style={{ borderRadius: 20 }}>
              <div className="modal-header border-bottom-0">
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setTosOpen(false)}
                />
              </div>
              <div className="modal-body pt-0">
                <div className="row px-2 mx-2 justify-content-center">
                  <div className="col-12 p-0 m-0">
                    <h1 className="text-center mt-0">Terms of Use</h1>
                  </div>
                  <div className="col-12 p-0 m-0">
                    Advanced Drainage Systems, Inc. (“ADS”) is not responsible for the accuracy,
                    appropriateness, or suitability of any product selected or purchased by the user.
                    ADS recommends that any designs, plans, or project specifications be reviewed by a
                    professional engineer, including any design engineer of record
                    <br />
                    <br />
                    The user is solely responsible for determining the suitability of any product for
                    its intended use and its compliance with any design plans, specifications, and/or
                    applicable laws, and the user assumes all risk and liability whatsoever in
                    connection therewith. ADS shall not be liable for any special, indirect, or
                    consequential damages incurred by the user or any third party.
                    <br />
                    <br />
                    All orders are subject to ADS’ Terms and Conditions of Sales:
                    <br />
                    <a
                      href="https://www.adspipe.com/terms-conditions/terms-and-conditions-sales"
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://www.adspipe.com/terms-conditions/terms-and-conditions-sales
                    </a>
                    .
                    <br />
                    Contact ADS for any questions. By using the FlexStorm Project Planner, user hereby
                    accepts these Terms&nbsp;of&nbsp;Use.
                    <br />
                    <p className="text-end">Last Updated: 06/24/2025</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
